import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  addAdminAircraft,
  adminCancelFlight,
  adminDeferComplaint,
  adminDeleteComplaint,
  adminExpediteComplaint,
  getAdminAircraft,
  getAdminAircraftDetail,
  getAdminComplaints,
  getAdminHistory,
  getAdminMostFlown,
  getAdminOverview,
  getAdminUsers,
  getAdminUserDetail,
  getComplaintActions,
  removeAdminAircraft,
  updateComplaint
} from '../services/api';
import AircraftStatusReport from './AircraftStatusReport';
import './AdminPanel.css';

const emptyAircraftForm = { registration: '', ac_type: '' };
const emptyDeferralForm = { category: 'MEL', deferral_type: 'C', due_days: '10', reason: '' };
const PREFIX_OPTIONS = ['HA-', '9H-', 'G-'];
const MAIN_VIEWS = new Set(['overview', 'complaints', 'fleet', 'users', 'ranking', 'history']);
const DETAIL_VIEWS = new Set(['aircraft', 'user']);
const ADMIN_WORKSPACE_STORAGE_KEY = 'etlb:admin-workspace:v2';

const readAdminWorkspace = () => {
  try {
    return JSON.parse(sessionStorage.getItem(ADMIN_WORKSPACE_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const updateAdminWorkspace = patch => {
  try {
    const current = readAdminWorkspace();
    sessionStorage.setItem(ADMIN_WORKSPACE_STORAGE_KEY, JSON.stringify({ ...current, ...patch }));
  } catch {
    // Session storage can be unavailable in privacy-restricted browsers.
  }
};

const statusLabel = status => {
  const value = String(status || '').toLowerCase();
  if (value === 'in_progress') return 'On Ground';
  if (value === 'post_flight') return 'Post Flight';
  if (value === 'completed') return 'Completed';
  if (value === 'cancelled') return 'Cancelled';
  return status || 'Unknown';
};

const formatDateTime = value => {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }).replace(',', '');
};

const formatNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('en-US') : '0';
};

const formatHours = value => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : '0.00';
};

const formatMinutesAsHours = value => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? (number / 60).toFixed(2) : '0.00';
};

const formatDuration = value => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return '--';
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return hours ? `${hours}h ${mins}m` : `${mins}m`;
};

const activeAge = minutes => {
  const total = Number(minutes);
  if (!Number.isFinite(total) || total < 0) return '--';
  return total < 60 ? `${Math.round(total)} min` : `${Math.floor(total / 60)}h ${Math.round(total % 60)}m`;
};

const getPilotLabel = item => (
  item?.pilot_name || item?.user_name || item?.top_user_name || item?.pilot_email || item?.email ||
  (item?.user_id ? `User #${item.user_id}` : '--')
);

const complaintState = complaint => {
  if (Number(complaint?.is_overdue) === 1) return 'overdue';
  return String(complaint?.status || 'unknown').toLowerCase();
};

const complaintStateLabel = complaint => {
  const value = complaintState(complaint);
  return value === 'overdue' ? 'Overdue' : statusLabel(value);
};

const ConfirmModal = ({ action, saving, error, onCancel, onConfirm }) => {
  if (!action) return null;
  return (
    <div className="admin-modal-backdrop" role="presentation" onMouseDown={() => !saving && onCancel()}>
      <div className="admin-modal" role="dialog" aria-modal="true" onMouseDown={event => event.stopPropagation()}>
        <div className="admin-modal-title">{action.title}</div>
        <div className="admin-modal-copy">{action.message}</div>
        {error ? <div className="admin-modal-error">{error}</div> : null}
        <div className="admin-modal-actions">
          <button type="button" className="admin-btn admin-btn-secondary" onClick={onCancel} disabled={saving}>Back</button>
          <button type="button" className={`admin-btn ${action.danger ? 'admin-btn-danger' : 'admin-btn-primary'}`} onClick={onConfirm} disabled={saving}>
            {saving ? 'Working...' : action.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPanel = ({ onDataChanged }) => {
  const initialWorkspace = useRef(readAdminWorkspace()).current;
  const initialView = MAIN_VIEWS.has(initialWorkspace.view) || DETAIL_VIEWS.has(initialWorkspace.view)
    ? initialWorkspace.view
    : 'overview';
  const panelRef = useRef(null);
  const restoredNavigationStack = Array.isArray(initialWorkspace.navigationStack)
    ? initialWorkspace.navigationStack.filter(item => MAIN_VIEWS.has(item?.view) || DETAIL_VIEWS.has(item?.view))
    : [];
  if (restoredNavigationStack.length === 0 && DETAIL_VIEWS.has(initialView)) {
    if (initialView === 'user' && initialWorkspace.returnAircraftId) {
      restoredNavigationStack.push({ view: 'aircraft', aircraftDetailId: String(initialWorkspace.returnAircraftId) });
    } else if (initialView === 'aircraft' && initialWorkspace.returnView === 'user' && initialWorkspace.userDetailId) {
      restoredNavigationStack.push({ view: 'user', userDetailId: String(initialWorkspace.userDetailId) });
    } else {
      restoredNavigationStack.push({
        view: MAIN_VIEWS.has(initialWorkspace.returnView) ? initialWorkspace.returnView : 'overview'
      });
    }
  }
  const [view, setView] = useState(initialView);
  const [overview, setOverview] = useState(null);
  const [aircraft, setAircraft] = useState([]);
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [recentHistory, setRecentHistory] = useState([]);
  const [history, setHistory] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [aircraftDetail, setAircraftDetail] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [navigationStack, setNavigationStack] = useState(restoredNavigationStack);
  const [aircraftDetailId, setAircraftDetailId] = useState(initialWorkspace.aircraftDetailId || null);
  const [userDetailId, setUserDetailId] = useState(initialWorkspace.userDetailId || null);

  const [historyAircraftId, setHistoryAircraftId] = useState(initialWorkspace.historyAircraftId || '');
  const [historyStatus, setHistoryStatus] = useState(initialWorkspace.historyStatus || 'all');
  const [historySearch, setHistorySearch] = useState(initialWorkspace.historySearch || '');
  const [rankingType, setRankingType] = useState(initialWorkspace.rankingType || '');
  const [rankingPrefix, setRankingPrefix] = useState(initialWorkspace.rankingPrefix || '');
  const [rankingSearch, setRankingSearch] = useState(initialWorkspace.rankingSearch || '');
  const [fleetSearch, setFleetSearch] = useState(initialWorkspace.fleetSearch || '');
  const [fleetStatus, setFleetStatus] = useState(initialWorkspace.fleetStatus || 'ALL');

  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUserRoles, setAdminUserRoles] = useState([]);
  const [adminUserSummary, setAdminUserSummary] = useState({});
  const [adminUserSearch, setAdminUserSearch] = useState(initialWorkspace.adminUserSearch || '');
  const [adminUserRole, setAdminUserRole] = useState(initialWorkspace.adminUserRole || '');
  const [adminUserStatus, setAdminUserStatus] = useState(initialWorkspace.adminUserStatus || 'all');

  const [complaints, setComplaints] = useState([]);
  const [complaintSummary, setComplaintSummary] = useState({});
  const [complaintStatus, setComplaintStatus] = useState(initialWorkspace.complaintStatus || 'ALL');
  const [complaintAircraftId, setComplaintAircraftId] = useState(initialWorkspace.complaintAircraftId || '');
  const [complaintSearch, setComplaintSearch] = useState(initialWorkspace.complaintSearch || '');
  const [selectedComplaintId, setSelectedComplaintId] = useState(initialWorkspace.selectedComplaintId || null);
  const [complaintActions, setComplaintActions] = useState([]);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [deferralTarget, setDeferralTarget] = useState(null);
  const [deferralForm, setDeferralForm] = useState(emptyDeferralForm);

  const [aircraftForm, setAircraftForm] = useState(emptyAircraftForm);
  const [addAircraftOpen, setAddAircraftOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const activeAircraft = useMemo(() => aircraft.filter(item => Number(item.is_active) === 1), [aircraft]);
  const typeOptions = useMemo(() => (
    [...new Set(aircraftTypes.map(type => String(type || '').trim().toUpperCase()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [aircraftTypes]);
  const filteredAircraft = useMemo(() => {
    const term = fleetSearch.trim().toUpperCase();
    return activeAircraft.filter(item => {
      const isAog = Number(item.open_items) > 0;
      if (fleetStatus === 'AOG' && !isAog) return false;
      if (fleetStatus === 'OPERATIONAL' && isAog) return false;
      if (!term) return true;
      return String(item.registration || '').toUpperCase().includes(term) ||
        String(item.ac_type || '').toUpperCase().includes(term);
    });
  }, [activeAircraft, fleetSearch, fleetStatus]);
  const aogAircraftCount = useMemo(
    () => activeAircraft.filter(item => Number(item.open_items) > 0).length,
    [activeAircraft]
  );
  const selectedComplaint = useMemo(
    () => complaints.find(item => String(item.id) === String(selectedComplaintId)) || null,
    [complaints, selectedComplaintId]
  );
  const selectedHistoryAircraft = activeAircraft.find(item => String(item.id) === String(historyAircraftId));

  const clearNotices = () => {
    setError('');
    setMessage('');
  };

  const loadBaseData = async () => {
    const [overviewRes, aircraftRes, recentRes] = await Promise.all([
      getAdminOverview(), getAdminAircraft(), getAdminHistory({ status: 'all', limit: 10 })
    ]);
    setOverview(overviewRes.data.data || null);
    setAircraft(aircraftRes.data.aircraft || []);
    setAircraftTypes(aircraftRes.data.types || []);
    setRecentHistory(recentRes.data.flights || []);
  };

  const loadHistory = async (override = {}) => {
    setHistoryLoading(true);
    try {
      const filters = {
        aircraft_id: override.aircraft_id !== undefined ? override.aircraft_id : historyAircraftId,
        status: override.status !== undefined ? override.status : historyStatus,
        search: override.search !== undefined ? override.search : historySearch
      };
      const res = await getAdminHistory({
        aircraft_id: filters.aircraft_id || undefined,
        status: filters.status,
        search: String(filters.search || '').trim() || undefined,
        limit: 250
      });
      setHistory(res.data.flights || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadRanking = async (override = {}) => {
    setRankingLoading(true);
    try {
      const filters = {
        ac_type: override.ac_type !== undefined ? override.ac_type : rankingType,
        prefix: override.prefix !== undefined ? override.prefix : rankingPrefix,
        search: override.search !== undefined ? override.search : rankingSearch
      };
      const res = await getAdminMostFlown({
        ac_type: filters.ac_type || undefined,
        prefix: filters.prefix || undefined,
        search: String(filters.search || '').trim() || undefined,
        limit: 500
      });
      setRanking(res.data.aircraft || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load ranking');
    } finally {
      setRankingLoading(false);
    }
  };

  const loadUsers = async (override = {}) => {
    setUsersLoading(true);
    try {
      const filters = {
        search: override.search !== undefined ? override.search : adminUserSearch,
        role: override.role !== undefined ? override.role : adminUserRole,
        status: override.status !== undefined ? override.status : adminUserStatus
      };
      const res = await getAdminUsers({
        search: String(filters.search || '').trim() || undefined,
        role: filters.role || undefined,
        status: filters.status,
        limit: 500
      });
      setAdminUsers(res.data.users || []);
      setAdminUserRoles(res.data.roles || []);
      setAdminUserSummary(res.data.summary || {});
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const loadComplaints = async (override = {}) => {
    setComplaintsLoading(true);
    try {
      const filters = {
        status: override.status !== undefined ? override.status : complaintStatus,
        aircraft_id: override.aircraft_id !== undefined ? override.aircraft_id : complaintAircraftId,
        search: override.search !== undefined ? override.search : complaintSearch
      };
      const res = await getAdminComplaints({
        status: filters.status,
        aircraft_id: filters.aircraft_id || undefined,
        search: String(filters.search || '').trim() || undefined,
        limit: 500
      });
      const nextComplaints = res.data.complaints || [];
      setComplaints(nextComplaints);
      setComplaintSummary(res.data.summary || {});
      setSelectedComplaintId(current => (
        current && nextComplaints.some(item => String(item.id) === String(current)) ? current : nextComplaints[0]?.id || null
      ));
      return nextComplaints;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load complaints');
    } finally {
      setComplaintsLoading(false);
    }
  };

  const loadAircraftDetail = async id => {
    if (!id) return;
    setAircraftDetailId(String(id));
    setDetailLoading(true);
    try {
      const res = await getAdminAircraftDetail(id);
      setAircraftDetail(res.data.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load aircraft detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const loadUserDetail = async id => {
    if (!id) return;
    setUserDetailId(String(id));
    setUserLoading(true);
    try {
      const res = await getAdminUserDetail(id);
      setUserDetail(res.data.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load user detail');
    } finally {
      setUserLoading(false);
    }
  };

  const refreshCurrent = async () => {
    clearNotices();
    setLoading(true);
    try {
      await loadBaseData();
      if (view === 'history') await loadHistory();
      if (view === 'ranking') await loadRanking();
      if (view === 'users') await loadUsers();
      if (view === 'complaints') await loadComplaints();
      if (view === 'aircraft' && aircraftDetailId) await loadAircraftDetail(aircraftDetailId);
      if (view === 'user' && userDetailId) await loadUserDetail(userDetailId);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshCurrent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateAdminWorkspace({
      view,
      navigationStack,
      aircraftDetailId,
      userDetailId,
      historyAircraftId,
      historyStatus,
      historySearch,
      rankingType,
      rankingPrefix,
      rankingSearch,
      fleetSearch,
      fleetStatus,
      adminUserSearch,
      adminUserRole,
      adminUserStatus,
      complaintStatus,
      complaintAircraftId,
      complaintSearch,
      selectedComplaintId
    });
  }, [
    adminUserRole, adminUserSearch, adminUserStatus, aircraftDetailId, complaintAircraftId, complaintSearch, complaintStatus, fleetSearch, fleetStatus,
    historyAircraftId, historySearch, historyStatus, navigationStack, rankingPrefix, rankingSearch, rankingType,
    selectedComplaintId, userDetailId, view
  ]);

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(() => {
      const saved = readAdminWorkspace().scrollPositions || {};
      if (panelRef.current) panelRef.current.scrollTop = Number(saved[`panel:${view}`] || 0);
      panelRef.current?.querySelectorAll('[data-admin-scroll-key]').forEach(element => {
        element.scrollTop = Number(saved[element.dataset.adminScrollKey] || 0);
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [complaintsLoading, detailLoading, loading, selectedComplaintId, userLoading, view]);

  const handleAdminScroll = event => {
    const target = event.target;
    const scrollKey = target === panelRef.current
      ? `panel:${view}`
      : target?.dataset?.adminScrollKey;
    if (!scrollKey) return;
    const current = readAdminWorkspace();
    updateAdminWorkspace({
      scrollPositions: {
        ...(current.scrollPositions || {}),
        [scrollKey]: target.scrollTop
      }
    });
  };

  useEffect(() => {
    if (view === 'history') loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, historyAircraftId, historyStatus]);

  useEffect(() => {
    if (view === 'ranking') loadRanking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, rankingType, rankingPrefix]);

  useEffect(() => {
    if (view === 'users') loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, adminUserRole, adminUserStatus]);

  useEffect(() => {
    if (view === 'complaints') loadComplaints();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, complaintStatus, complaintAircraftId]);

  useEffect(() => {
    setDescriptionDraft(selectedComplaint?.defect_description || '');
    if (!selectedComplaint?.id) {
      setComplaintActions([]);
      return;
    }
    getComplaintActions(selectedComplaint.id)
      .then(res => setComplaintActions(res.data.actions || []))
      .catch(() => setComplaintActions([]));
  }, [selectedComplaint?.id, selectedComplaint?.defect_description]);

  const setMainView = nextView => {
    setNavigationStack([]);
    setView(nextView);
  };

  const pushCurrentLocation = () => {
    const location = { view };
    if (view === 'aircraft' && aircraftDetailId) location.aircraftDetailId = String(aircraftDetailId);
    if (view === 'user' && userDetailId) location.userDetailId = String(userDetailId);
    setNavigationStack(current => [...current.slice(-19), location]);
  };

  const openAircraft = item => {
    const id = item?.aircraft_id || item?.id;
    if (!id) return;
    pushCurrentLocation();
    setAircraftDetail(null);
    setAircraftDetailId(String(id));
    setView('aircraft');
    loadAircraftDetail(id);
  };

  const openUser = userId => {
    if (!userId) return;
    pushCurrentLocation();
    setUserDetail(null);
    setUserDetailId(String(userId));
    setView('user');
    loadUserDetail(userId);
  };

  const goBack = () => {
    loadBaseData().catch(() => {});
    const previous = navigationStack[navigationStack.length - 1] || { view: 'overview' };
    setNavigationStack(current => current.slice(0, -1));

    if (previous.view === 'aircraft' && previous.aircraftDetailId) {
      setAircraftDetail(null);
      setAircraftDetailId(String(previous.aircraftDetailId));
      setView('aircraft');
      loadAircraftDetail(previous.aircraftDetailId);
      return;
    }
    if (previous.view === 'user' && previous.userDetailId) {
      setUserDetail(null);
      setUserDetailId(String(previous.userDetailId));
      setView('user');
      loadUserDetail(previous.userDetailId);
      return;
    }
    setView(MAIN_VIEWS.has(previous.view) ? previous.view : 'overview');
  };

  const requestCancelFlight = flight => setConfirmAction({
    type: 'cancel-flight', item: flight, title: 'Cancel active flight', danger: true,
    message: `Cancel ${flight.flight_number || `flight #${flight.id}`} on ${flight.registration || 'this aircraft'}? The flight data will be removed and the aircraft released.`,
    confirmLabel: 'Cancel Flight'
  });

  const requestRemoveAircraft = item => setConfirmAction({
    type: 'remove-aircraft', item, title: 'Remove aircraft', danger: true,
    message: `Remove ${item.registration} from the active fleet? Historic data will remain available.`,
    confirmLabel: 'Remove Aircraft'
  });

  const requestComplaintAction = (type, complaint) => {
    const labels = {
      'delete-complaint': {
        title: 'Permanently delete complaint', danger: true, confirmLabel: 'Delete Permanently',
        message: `Delete ${complaint.work_order_number || `complaint #${complaint.id}`} and its complete action and maintenance history? This cannot be undone.`
      },
      'expedite-complaint': {
        title: 'Expedite maintenance', confirmLabel: 'Expedite Now',
        message: `Move ${complaint.work_order_number || `complaint #${complaint.id}`} to immediate maintenance processing?`
      },
      'close-complaint': {
        title: 'Close complaint', confirmLabel: 'Close Item',
        message: `Close ${complaint.work_order_number || `complaint #${complaint.id}`} as completed?`
      },
      'reopen-complaint': {
        title: 'Reopen complaint', confirmLabel: 'Reopen Item',
        message: `Return ${complaint.work_order_number || `complaint #${complaint.id}`} to the open items list?`
      }
    };
    setConfirmAction({ type, item: complaint, ...labels[type] });
  };

  const reloadComplaintWorkspace = async preferredId => {
    const [nextComplaints] = await Promise.all([loadComplaints(), loadBaseData()]);
    if (preferredId && nextComplaints?.some(item => String(item.id) === String(preferredId))) {
      setSelectedComplaintId(preferredId);
    }
  };

  const handleConfirmAction = async () => {
    const item = confirmAction?.item;
    if (!item) return;
    setSaving(true);
    clearNotices();
    try {
      if (confirmAction.type === 'cancel-flight') {
        await adminCancelFlight(item.id);
        setMessage(`Flight ${item.flight_number || item.id} cancelled`);
        await loadBaseData();
      } else if (confirmAction.type === 'remove-aircraft') {
        await removeAdminAircraft(item.id);
        setMessage(`${item.registration} removed`);
        if (String(aircraftDetail?.aircraft?.id) === String(item.id)) {
          setView('fleet');
          setNavigationStack([]);
          setAircraftDetail(null);
        }
        await loadBaseData();
      } else if (confirmAction.type === 'delete-complaint') {
        await adminDeleteComplaint(item.id);
        setMessage(`${item.work_order_number || `Complaint #${item.id}`} permanently deleted`);
        setSelectedComplaintId(null);
        await reloadComplaintWorkspace();
      } else if (confirmAction.type === 'expedite-complaint') {
        await adminExpediteComplaint(item.id);
        setMessage(`${item.work_order_number || `Complaint #${item.id}`} moved to immediate maintenance`);
        await reloadComplaintWorkspace(item.id);
      } else if (confirmAction.type === 'close-complaint') {
        await updateComplaint(item.id, { status: 'CLOSED', section: 'closed', action_type: 'STATUS', action_text: 'WORKORDER CLOSED BY ADMIN.' });
        setMessage(`${item.work_order_number || `Complaint #${item.id}`} closed`);
        await reloadComplaintWorkspace(item.id);
      } else if (confirmAction.type === 'reopen-complaint') {
        await updateComplaint(item.id, { status: 'OPEN', section: 'open', action_type: 'STATUS', action_text: 'WORKORDER REOPENED BY ADMIN.' });
        setMessage(`${item.work_order_number || `Complaint #${item.id}`} reopened`);
        await reloadComplaintWorkspace(item.id);
      }
      setConfirmAction(null);
      onDataChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAircraft = async event => {
    event.preventDefault();
    setSaving(true);
    clearNotices();
    try {
      await addAdminAircraft(aircraftForm);
      setMessage(`${aircraftForm.registration.trim().toUpperCase()} added to the fleet`);
      setAircraftForm(emptyAircraftForm);
      setAddAircraftOpen(false);
      await loadBaseData();
      onDataChanged?.();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add aircraft');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComplaintLock = async complaint => {
    setSaving(true);
    clearNotices();
    try {
      const nextLocked = Number(complaint.is_locked) !== 1;
      await updateComplaint(complaint.id, { is_locked: nextLocked, action_type: 'LOCK' });
      setMessage(`${complaint.work_order_number || 'Complaint'} ${nextLocked ? 'locked' : 'unlocked'}`);
      await reloadComplaintWorkspace(complaint.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update complaint lock');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDescription = async () => {
    if (!selectedComplaint?.id || !descriptionDraft.trim()) return;
    setSaving(true);
    clearNotices();
    try {
      await updateComplaint(selectedComplaint.id, {
        defect_description: descriptionDraft.trim(), action_type: 'DESCRIPTION', action_text: 'DEFECT DESCRIPTION UPDATED BY ADMIN.'
      });
      setMessage('Defect description updated');
      await reloadComplaintWorkspace(selectedComplaint.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update description');
    } finally {
      setSaving(false);
    }
  };

  const handleAdminDefer = async event => {
    event.preventDefault();
    if (!deferralTarget) return;
    setSaving(true);
    clearNotices();
    try {
      await adminDeferComplaint(deferralTarget.id, deferralForm);
      setMessage(`${deferralTarget.work_order_number || 'Complaint'} deferred`);
      setDeferralTarget(null);
      setDeferralForm(emptyDeferralForm);
      await reloadComplaintWorkspace(deferralTarget.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to defer complaint');
    } finally {
      setSaving(false);
    }
  };

  const regButton = (item, label, from = view) => (
    <button type="button" className="admin-reg-button" onClick={() => openAircraft(item, from)}>
      {label || item?.registration || '--'}
    </button>
  );

  const userButton = (userId, label, from = view) => {
    if (!userId) return <span className="admin-empty-value">--</span>;
    return (
      <button type="button" className="admin-user-button" onClick={() => openUser(userId, from)}>
        {label || `User #${userId}`}
      </button>
    );
  };

  const field = (label, value) => (
    <div className="admin-field">
      <span>{label}</span>
      <div>{value === null || value === undefined || value === '' ? '--' : value}</div>
    </div>
  );

  const statusPill = status => (
    <span className={`admin-status-pill admin-status-${String(status || 'unknown').replace('_', '-')}`}>{statusLabel(status)}</span>
  );

  const complaintPill = complaint => (
    <span className={`admin-status-pill admin-status-${complaintState(complaint)}`}>{complaintStateLabel(complaint)}</span>
  );

  const empty = label => <div className="admin-empty-state">{label}</div>;

  const renderHeader = (title, subtitle, actions = null) => (
    <div className="admin-console-header">
      <div>
        <div className="admin-console-subtitle">{subtitle}</div>
        <h1>{title}</h1>
      </div>
      <div className="admin-header-actions">
        {actions}
        {!MAIN_VIEWS.has(view) ? <button type="button" className="admin-btn admin-btn-secondary" onClick={goBack}>Back</button> : null}
        <button type="button" className="admin-btn admin-btn-secondary" onClick={refreshCurrent} disabled={loading || saving}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
    </div>
  );

  const renderTabs = () => (
    <div className="admin-tabs" role="tablist" aria-label="Admin views">
      <button type="button" className={view === 'overview' ? 'active' : ''} onClick={() => setMainView('overview')}>Overview</button>
      <button type="button" className={view === 'complaints' ? 'active' : ''} onClick={() => setMainView('complaints')}>Complaints</button>
      <button type="button" className={view === 'fleet' ? 'active' : ''} onClick={() => setMainView('fleet')}>Fleet</button>
      <button type="button" className={view === 'users' ? 'active' : ''} onClick={() => setMainView('users')}>Users</button>
      <button type="button" className={view === 'ranking' ? 'active' : ''} onClick={() => setMainView('ranking')}>Analytics</button>
      <button type="button" className={view === 'history' ? 'active' : ''} onClick={() => setMainView('history')}>Flight History</button>
    </div>
  );

  const renderKpis = stats => (
    <section className="admin-kpi-grid">
      {stats.map(stat => (
        <div className={`admin-kpi ${stat.tone ? `admin-kpi-${stat.tone}` : ''}`} key={stat.label}>
          <span>{stat.label}</span>
          <strong>{stat.value}</strong>
        </div>
      ))}
    </section>
  );

  const renderFlightRecord = (flight, from = view) => (
    <article className="admin-record" key={flight.id}>
      <div className="admin-record-head">
        <div className="admin-record-identity">
          {regButton(flight, flight.registration || '--', from)}
          <div><strong>{flight.flight_number || `#${flight.id}`}</strong><span>{flight.from_airport || '--'} - {flight.to_airport || '--'}</span></div>
        </div>
        {statusPill(flight.status)}
      </div>
      <div className="admin-field-grid">
        {field('Date', formatDateTime(flight.date_of_flight))}
        {field('Pilot', userButton(flight.user_id, getPilotLabel(flight), from))}
        {field('Block', formatDuration(flight.block_time))}
        {field('Flight', formatDuration(flight.flight_time))}
        {field('Landings', formatNumber(flight.total_landings))}
        {field('Complaints', formatNumber(flight.complaints_count))}
      </div>
    </article>
  );

  const renderRankRow = (item, from = view, hideUser = false) => (
    <article className={`admin-rank-row ${hideUser ? 'admin-rank-row-no-user' : ''}`} key={item.id}>
      <div className="admin-rank-position"><strong>{formatNumber(item.completed_flights)}</strong><span>Flights</span></div>
      <div className="admin-rank-aircraft">
        {regButton(item, item.registration, from)}
        <span>{item.ac_type || '--'}</span>
      </div>
      <div className="admin-rank-metric"><span>FH</span><strong>{formatHours(item.flight_hours)}</strong></div>
      <div className="admin-rank-metric"><span>FC</span><strong>{formatNumber(item.flight_cycles)}</strong></div>
      {!hideUser ? <div className="admin-rank-user"><span>Top user</span>{userButton(item.top_user_id, item.top_user_name, from)}</div> : null}
    </article>
  );

  const renderLiveFlights = activeFlights => (
    <section className="admin-section admin-live-section">
      <div className="admin-section-head">
        <div><h2>Live Flights</h2><p>{activeFlights.length} aircraft currently assigned</p></div>
        <span className="admin-live-indicator">Live</span>
      </div>
      <div className="admin-live-list admin-five-row-list" data-admin-scroll-key="overview-live-flights">
        {activeFlights.length ? activeFlights.map(flight => (
          <article className="admin-live-row" key={flight.id}>
            <div className="admin-live-primary">
              {regButton(flight, flight.registration || '--', 'overview')}
              <div><strong>{flight.flight_number || `#${flight.id}`}</strong><span>{flight.from_airport || '--'} - {flight.to_airport || '--'}</span></div>
            </div>
            <div className="admin-live-meta"><span>Pilot</span>{userButton(flight.user_id, getPilotLabel(flight), 'overview')}</div>
            <div className="admin-live-meta"><span>Updated</span><strong>{activeAge(flight.minutes_since_update)}</strong></div>
            {statusPill(flight.status)}
            <button type="button" className="admin-btn admin-btn-danger admin-btn-compact" onClick={() => requestCancelFlight(flight)} disabled={saving}>Cancel</button>
          </article>
        )) : empty('No active flights')}
      </div>
    </section>
  );

  const renderOverview = () => {
    const data = overview || {};
    const summary = data.summary || {};
    const activeFlights = data.activeFlights || [];
    const mostFlown = data.mostFlown || [];
    return (
      <>
        {renderHeader('Operations Control', 'Admin Panel', (
          <button type="button" className="admin-btn admin-btn-primary" onClick={() => setAddAircraftOpen(true)}>Add Aircraft</button>
        ))}
        {renderTabs()}
        {renderKpis([
          { label: 'Active Flights', value: formatNumber(summary.active_flights), tone: 'live' },
          { label: 'Fleet Aircraft', value: formatNumber(summary.active_aircraft) },
          { label: 'Open Complaints', value: formatNumber(summary.open_complaints), tone: 'attention' },
          { label: 'Completed 30d', value: formatNumber(summary.completed_last_30) }
        ])}
        {renderLiveFlights(activeFlights)}
        <section className="admin-section">
          <div className="admin-section-head">
            <div><h2>Quick Management</h2><p>Primary administrative workspaces</p></div>
          </div>
          <div className="admin-quick-actions">
            <button type="button" onClick={() => setMainView('complaints')}><strong>Complaint Control</strong><span>{formatNumber(summary.open_complaints)} active items</span></button>
            <button type="button" onClick={() => setMainView('fleet')}><strong>Fleet Management</strong><span>{formatNumber(summary.active_aircraft)} active aircraft</span></button>
            <button type="button" onClick={() => setMainView('users')}><strong>User Directory</strong><span>Accounts and activity</span></button>
            <button type="button" onClick={() => setMainView('history')}><strong>Flight Records</strong><span>Search full history</span></button>
          </div>
        </section>
        <section className="admin-section">
          <div className="admin-section-head">
            <div><h2>Fleet Activity</h2><p>Most used aircraft</p></div>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setMainView('ranking')}>View Analytics</button>
          </div>
          <div className="admin-rank-list admin-five-row-list" data-admin-scroll-key="overview-fleet-activity">
            {mostFlown.length ? mostFlown.map(item => renderRankRow(item, 'overview')) : empty('No fleet activity')}
          </div>
        </section>
        <section className="admin-section">
          <div className="admin-section-head">
            <div><h2>Recent Flights</h2><p>Latest fleet records</p></div>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setMainView('history')}>View History</button>
          </div>
          <div className="admin-record-list admin-preview-list admin-five-row-list" data-admin-scroll-key="overview-recent-flights">
            {recentHistory.length ? recentHistory.map(flight => renderFlightRecord(flight, 'overview')) : empty('No recent flights')}
          </div>
        </section>
      </>
    );
  };

  const renderComplaintList = () => (
    <div className="admin-complaint-list admin-five-row-list" data-admin-scroll-key="complaint-list">
      {complaintsLoading && !complaints.length ? <div className="admin-loading-inline"><div className="spinner" /></div> : null}
      {!complaintsLoading && !complaints.length ? empty('No complaints match these filters') : null}
      {complaints.map(complaint => (
        <button
          type="button"
          className={`admin-complaint-row ${String(selectedComplaintId) === String(complaint.id) ? 'selected' : ''}`}
          key={complaint.id}
          onClick={() => setSelectedComplaintId(complaint.id)}
        >
          <span className="admin-complaint-row-main">
            <span><strong>{complaint.work_order_number || `Complaint #${complaint.id}`}</strong>{complaintPill(complaint)}</span>
            <span>{complaint.defect_description || 'No description'}</span>
          </span>
          <span className="admin-complaint-row-meta"><strong>{complaint.registration || '--'}</strong><span>ATA {complaint.ata_chapter || '--'}</span></span>
          <span className="admin-complaint-row-meta"><strong>{complaint.created_by_name || complaint.created_by_email || '--'}</strong><span>{formatDateTime(complaint.opened_at || complaint.created_at)}</span></span>
          <span className="admin-complaint-row-meta"><strong>{complaint.due_at ? formatDateTime(complaint.due_at) : 'No due date'}</strong><span>{Number(complaint.is_locked) === 1 ? 'Locked' : complaint.maintenance_planned_close_at ? `Maint. ${formatDateTime(complaint.maintenance_planned_close_at)}` : 'No maintenance plan'}</span></span>
        </button>
      ))}
    </div>
  );

  const renderComplaintInspector = () => {
    const complaint = selectedComplaint;
    if (!complaint) return <div className="admin-complaint-inspector admin-empty-inspector">Select a complaint</div>;
    const isClosed = String(complaint.status || '').toUpperCase() === 'CLOSED';
    const isOpen = String(complaint.status || '').toUpperCase() === 'OPEN';
    const isLocked = Number(complaint.is_locked) === 1;
    return (
      <div className="admin-complaint-inspector">
        <div className="admin-inspector-head">
          <div>
            <span>Workorder</span>
            <h2>{complaint.work_order_number || `Complaint #${complaint.id}`}</h2>
          </div>
          <div className="admin-inspector-state">{complaintPill(complaint)}{isLocked ? <span className="admin-status-pill admin-status-locked">Locked</span> : null}</div>
        </div>
        <div className="admin-inspector-links">
          {regButton(complaint, complaint.registration || '--', 'complaints')}
          {userButton(complaint.created_by, complaint.created_by_name || complaint.created_by_email, 'complaints')}
        </div>
        <div className="admin-inspector-grid">
          {field('Aircraft Type', complaint.ac_type)}
          {field('Flight', complaint.flight_number || (complaint.flight_id ? `#${complaint.flight_id}` : '--'))}
          {field('ATA', complaint.ata_chapter ? `${complaint.ata_chapter} ${complaint.ata_description || ''}`.trim() : '--')}
          {field('Category', complaint.deferral_category || complaint.category || complaint.type)}
          {field('Opened', formatDateTime(complaint.opened_at || complaint.created_at))}
          {field('Due', complaint.due_at ? formatDateTime(complaint.due_at) : '--')}
          {field('Maintenance Plan', complaint.maintenance_planned_close_at ? formatDateTime(complaint.maintenance_planned_close_at) : '--')}
          {field('Plan State', complaint.maintenance_plan_type || '--')}
        </div>
        <div className="admin-description-editor">
          <label htmlFor="admin-complaint-description">Defect Description</label>
          <textarea id="admin-complaint-description" value={descriptionDraft} onChange={event => setDescriptionDraft(event.target.value)} />
          <button type="button" className="admin-btn admin-btn-secondary" onClick={handleSaveDescription} disabled={saving || !descriptionDraft.trim() || descriptionDraft.trim() === String(complaint.defect_description || '').trim()}>Save Description</button>
        </div>
        <div className="admin-action-toolbar">
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => handleToggleComplaintLock(complaint)} disabled={saving}>{isLocked ? 'Unlock' : 'Lock'}</button>
          {isOpen ? <button type="button" className="admin-btn admin-btn-primary" onClick={() => { setDeferralTarget(complaint); setDeferralForm(emptyDeferralForm); }} disabled={saving || Number(complaint.dispatch_blocked) === 1}>Defer Item</button> : null}
          {!isClosed ? <button type="button" className="admin-btn admin-btn-primary" onClick={() => requestComplaintAction('expedite-complaint', complaint)} disabled={saving || isLocked}>Expedite Maintenance</button> : null}
          {!isClosed ? <button type="button" className="admin-btn admin-btn-secondary" onClick={() => requestComplaintAction('close-complaint', complaint)} disabled={saving}>Close</button> : <button type="button" className="admin-btn admin-btn-secondary" onClick={() => requestComplaintAction('reopen-complaint', complaint)} disabled={saving}>Reopen</button>}
          <button type="button" className="admin-btn admin-btn-danger" onClick={() => requestComplaintAction('delete-complaint', complaint)} disabled={saving}>Delete</button>
        </div>
        {Number(complaint.dispatch_blocked) === 1 && isOpen ? <div className="admin-inline-warning">NO DISPATCH item. Deferral is blocked.</div> : null}
        <div className="admin-audit">
          <div className="admin-subhead"><h3>Audit Trail</h3><span>{complaintActions.length} actions</span></div>
          <div className="admin-audit-list admin-five-row-list" data-admin-scroll-key={`complaint-audit-${complaint.id}`}>
            {complaintActions.length ? [...complaintActions].reverse().map(action => (
              <div className="admin-audit-row" key={action.id}>
                <span>{formatDateTime(action.performed_at)}</span>
                <div><strong>{action.action_text || action.action_type}</strong><span>{action.performed_by_name || 'System'}</span></div>
              </div>
            )) : empty('No recorded actions')}
          </div>
        </div>
      </div>
    );
  };

  const renderComplaints = () => (
    <>
      {renderHeader('Complaint Control', 'Admin Panel')}
      {renderTabs()}
      {renderKpis([
        { label: 'Open', value: formatNumber(complaintSummary.open_items) },
        { label: 'Deferred', value: formatNumber(complaintSummary.deferred_items) },
        { label: 'Overdue', value: formatNumber(complaintSummary.overdue_items), tone: 'danger' },
        { label: 'Locked', value: formatNumber(complaintSummary.locked_items) },
        { label: 'Closed', value: formatNumber(complaintSummary.closed_items) }
      ])}
      <section className="admin-section admin-complaint-workspace">
        <form className="admin-filter-bar" onSubmit={event => { event.preventDefault(); loadComplaints(); }}>
          <select value={complaintStatus} onChange={event => setComplaintStatus(event.target.value)}>
            <option value="ALL">All statuses</option><option value="OPEN">Open</option><option value="DEFERRED">Deferred</option><option value="OVERDUE">Overdue</option><option value="CLOSED">Closed</option>
          </select>
          <select value={complaintAircraftId} onChange={event => setComplaintAircraftId(event.target.value)}>
            <option value="">All aircraft</option>{activeAircraft.map(item => <option value={item.id} key={item.id}>{item.registration}</option>)}
          </select>
          <input type="search" placeholder="Search workorder, ATA, aircraft, creator" value={complaintSearch} onChange={event => setComplaintSearch(event.target.value)} />
          <button type="submit" className="admin-btn admin-btn-secondary" disabled={complaintsLoading}>{complaintsLoading ? 'Loading...' : 'Search'}</button>
        </form>
        <div className="admin-complaint-layout">
          {renderComplaintList()}
          {renderComplaintInspector()}
        </div>
      </section>
    </>
  );

  const renderFleet = () => (
    <>
      {renderHeader('Fleet Management', 'Admin Panel', (
        <button type="button" className="admin-btn admin-btn-primary" onClick={() => setAddAircraftOpen(true)}>Add Aircraft</button>
      ))}
      {renderTabs()}
      {renderKpis([
        { label: 'Active Fleet', value: formatNumber(activeAircraft.length) },
        { label: 'AOG', value: formatNumber(aogAircraftCount), tone: 'danger' },
        { label: 'Operational', value: formatNumber(activeAircraft.length - aogAircraftCount) }
      ])}
      <section className="admin-section">
        <div className="admin-section-head"><div><h2>Active Fleet</h2><p>{filteredAircraft.length} aircraft shown</p></div></div>
        <div className="admin-fleet-toolbar">
          <input type="search" className="admin-search" placeholder="Search registration or aircraft type" value={fleetSearch} onChange={event => setFleetSearch(event.target.value)} />
          <select value={fleetStatus} onChange={event => setFleetStatus(event.target.value)}>
            <option value="ALL">All fleet status</option>
            <option value="AOG">AOG</option>
            <option value="OPERATIONAL">Operational</option>
          </select>
          <button type="button" className="admin-btn admin-btn-primary" onClick={() => setAddAircraftOpen(true)}>Add Aircraft</button>
        </div>
        <div className="admin-fleet-list" data-admin-scroll-key="fleet-list">
          {filteredAircraft.length ? filteredAircraft.map(item => (
            <article className={`admin-fleet-row ${Number(item.open_items) > 0 ? 'admin-fleet-row-aog' : ''}`} key={item.id}>
              <div className="admin-record-identity">{regButton(item, item.registration, 'fleet')}<div><strong>{item.ac_type || '--'}</strong><span>Last flight {formatDateTime(item.last_flight_at)}</span></div></div>
              <div className="admin-fleet-metric"><span>Flights</span><strong>{formatNumber(item.total_flights)}</strong></div>
              <div className="admin-fleet-metric"><span>Open Items</span><strong>{formatNumber(item.open_items)}</strong></div>
              <span className={`admin-status-pill ${Number(item.open_items) > 0 ? 'admin-status-aog' : 'admin-status-operational'}`}>{Number(item.open_items) > 0 ? 'AOG' : 'Operational'}</span>
              <button type="button" className="admin-btn admin-btn-danger" onClick={() => requestRemoveAircraft(item)} disabled={saving || Number(item.active_flights) > 0}>Remove</button>
            </article>
          )) : empty('No aircraft match this search')}
        </div>
      </section>
    </>
  );

  const renderUsers = () => (
    <>
      {renderHeader('User Directory', 'Admin Panel')}
      {renderTabs()}
      {renderKpis([
        { label: 'All Users', value: formatNumber(adminUserSummary.total_users) },
        { label: 'Active', value: formatNumber(adminUserSummary.active_users) },
        { label: 'Inactive', value: formatNumber(adminUserSummary.inactive_users), tone: adminUserSummary.inactive_users ? 'attention' : '' },
        { label: 'Admins', value: formatNumber(adminUserSummary.admin_users) }
      ])}
      <section className="admin-section">
        <form className="admin-filter-bar" onSubmit={event => { event.preventDefault(); loadUsers(); }}>
          <input
            type="search"
            placeholder="Search name, email, or user ID"
            value={adminUserSearch}
            onChange={event => setAdminUserSearch(event.target.value)}
          />
          <select value={adminUserRole} onChange={event => setAdminUserRole(event.target.value)}>
            <option value="">All roles</option>
            {adminUserRoles.map(role => <option value={role} key={role}>{role}</option>)}
          </select>
          <select value={adminUserStatus} onChange={event => setAdminUserStatus(event.target.value)}>
            <option value="all">All account statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <button type="submit" className="admin-btn admin-btn-secondary" disabled={usersLoading}>{usersLoading ? 'Loading...' : 'Search'}</button>
        </form>
        <div className="admin-user-directory admin-five-row-list" data-admin-scroll-key="user-directory">
          {usersLoading && !adminUsers.length ? <div className="admin-loading-inline"><div className="spinner" /></div> : null}
          {!usersLoading && !adminUsers.length ? empty('No users match these filters') : null}
          {adminUsers.map(item => (
            <article className="admin-user-directory-row" key={item.id}>
              <div className="admin-directory-identity">
                {userButton(item.id, item.full_name || item.email || `User #${item.id}`, 'users')}
                <span>{item.email || '--'} | ID {item.id}</span>
              </div>
              <div className="admin-directory-metric"><span>Role</span><strong>{item.role || '--'}</strong></div>
              <div className="admin-directory-metric"><span>Flights</span><strong>{formatNumber(item.total_flights)}</strong></div>
              <div className="admin-directory-metric"><span>Block Hrs</span><strong>{formatMinutesAsHours(item.block_minutes)}</strong></div>
              <div className="admin-directory-metric"><span>Complaints</span><strong>{formatNumber(item.complaints_count)}{Number(item.open_complaints) > 0 ? ` (${formatNumber(item.open_complaints)} open)` : ''}</strong></div>
              <div className="admin-directory-metric"><span>Last Flight</span><strong>{formatDateTime(item.last_flight_at)}</strong></div>
              <span className={`admin-status-pill ${Number(item.is_active) === 1 ? 'admin-status-operational' : 'admin-status-locked'}`}>{Number(item.is_active) === 1 ? 'Active' : 'Inactive'}</span>
            </article>
          ))}
        </div>
      </section>
    </>
  );

  const renderRanking = () => (
    <>
      {renderHeader('Fleet Analytics', 'Admin Panel')}
      {renderTabs()}
      <section className="admin-section">
        <form className="admin-filter-bar" onSubmit={event => { event.preventDefault(); loadRanking(); }}>
          <select value={rankingType} onChange={event => setRankingType(event.target.value)}><option value="">All aircraft types</option>{typeOptions.map(type => <option value={type} key={type}>{type}</option>)}</select>
          <select value={rankingPrefix} onChange={event => setRankingPrefix(event.target.value)}><option value="">All prefixes</option>{PREFIX_OPTIONS.map(prefix => <option value={prefix} key={prefix}>{prefix}</option>)}</select>
          <input type="search" placeholder="Search registration" value={rankingSearch} onChange={event => setRankingSearch(event.target.value)} />
          <button type="submit" className="admin-btn admin-btn-secondary" disabled={rankingLoading}>{rankingLoading ? 'Loading...' : 'Search'}</button>
        </form>
        <div className="admin-rank-list admin-five-row-list" data-admin-scroll-key="analytics-ranking">{ranking.length ? ranking.map(item => renderRankRow(item, 'ranking')) : empty('No ranking data')}</div>
      </section>
    </>
  );

  const renderHistory = () => (
    <>
      {renderHeader(selectedHistoryAircraft ? `${selectedHistoryAircraft.registration} History` : 'Flight History', 'Admin Panel')}
      {renderTabs()}
      <section className="admin-section">
        <form className="admin-filter-bar" onSubmit={event => { event.preventDefault(); loadHistory(); }}>
          <select value={historyAircraftId} onChange={event => setHistoryAircraftId(event.target.value)}><option value="">All aircraft</option>{activeAircraft.map(item => <option value={item.id} key={item.id}>{item.registration}</option>)}</select>
          <select value={historyStatus} onChange={event => setHistoryStatus(event.target.value)}><option value="all">All statuses</option><option value="in_progress">On Ground</option><option value="post_flight">Post Flight</option><option value="completed">Completed</option></select>
          <input type="search" placeholder="Search flight, route, pilot" value={historySearch} onChange={event => setHistorySearch(event.target.value)} />
          <button type="submit" className="admin-btn admin-btn-secondary" disabled={historyLoading}>{historyLoading ? 'Loading...' : 'Search'}</button>
        </form>
        <div className="admin-record-list admin-five-row-list" data-admin-scroll-key="flight-history">{history.length ? history.map(flight => renderFlightRecord(flight, 'history')) : empty('No history records')}</div>
      </section>
    </>
  );

  const renderAircraft = () => {
    const detail = aircraftDetail;
    const ac = detail?.aircraft;
    if (detailLoading && !detail) return <>{renderHeader('Aircraft Detail', 'Admin Panel')}<div className="admin-loading-inline"><div className="spinner" /></div></>;
    if (!ac) return <>{renderHeader('Aircraft Detail', 'Admin Panel')}<section className="admin-section">{empty('Aircraft detail not loaded')}</section></>;
    return (
      <>
        {renderHeader(ac.registration, ac.ac_type || 'Aircraft Detail', (
          <button type="button" className="admin-btn admin-btn-danger" onClick={() => requestRemoveAircraft(ac)} disabled={saving || Number(detail.summary?.active_flights) > 0}>Remove Aircraft</button>
        ))}
        {renderKpis([
          { label: 'Open', value: formatNumber(detail.summary.open_items) }, { label: 'Deferred', value: formatNumber(detail.summary.deferred_items) },
          { label: 'Overdue', value: formatNumber(detail.summary.overdue_items), tone: 'danger' }, { label: 'Flights', value: formatNumber(detail.summary.total_flights) },
          { label: 'Block Hrs', value: formatMinutesAsHours(detail.summary.block_minutes) }
        ])}
        <section className="admin-section">
          <div className="admin-section-head"><div><h2>Aircraft Items</h2><p>Workorders and maintenance controls</p></div></div>
          <div className="admin-maintenance-frame"><AircraftStatusReport aircraftId={ac.id} showFlyButton={false} embeddedMode rootTitle="Aircraft Items" backLabel="Admin" onBack={goBack} /></div>
        </section>
        <section className="admin-section">
          <div className="admin-section-head"><div><h2>Most Active Users</h2><p>Flight activity on {ac.registration}</p></div></div>
          <div className="admin-record-list admin-five-row-list" data-admin-scroll-key={`aircraft-users-${ac.id}`}>
            {detail.topUsers?.length ? detail.topUsers.map(item => (
              <article className="admin-record" key={item.user_id}>
                <div className="admin-record-head">{userButton(item.user_id, item.user_name, 'aircraft')}</div>
                <div className="admin-field-grid">{field('Flights', formatNumber(item.total_flights))}{field('Completed', formatNumber(item.completed_flights))}{field('Block Hrs', formatMinutesAsHours(item.block_minutes))}{field('Last Flight', formatDateTime(item.last_flight_at))}</div>
              </article>
            )) : empty('No user flight data')}
          </div>
        </section>
        <section className="admin-section"><div className="admin-section-head"><div><h2>Recent Flights</h2><p>Aircraft flight history</p></div></div><div className="admin-record-list admin-five-row-list" data-admin-scroll-key={`aircraft-flights-${ac.id}`}>{detail.recentFlights?.length ? detail.recentFlights.map(flight => renderFlightRecord(flight, 'aircraft')) : empty('No recent flights')}</div></section>
      </>
    );
  };

  const renderUser = () => {
    const detail = userDetail;
    const user = detail?.user;
    if (userLoading && !detail) return <>{renderHeader('User Detail', 'Admin Panel')}<div className="admin-loading-inline"><div className="spinner" /></div></>;
    if (!user) return <>{renderHeader('User Detail', 'Admin Panel')}<section className="admin-section">{empty('User detail not loaded')}</section></>;
    const summary = detail.summary || {};
    const complaintStats = detail.complaintSummary || {};
    const delayStats = detail.delaySummary || {};
    const completionRate = Number(summary.total_flights) > 0
      ? `${Math.round((Number(summary.completed_flights || 0) / Number(summary.total_flights)) * 100)}%`
      : '0%';
    return (
      <>
        {renderHeader(user.full_name || user.email || `User #${user.id}`, 'User Flight Data')}
        <section className="admin-profile-strip">
          {field('Email', user.email)}
          {field('Role', user.role)}
          {field('Account', Number(user.is_active) === 1 ? 'Active' : 'Inactive')}
          {field('First Flight', formatDateTime(summary.first_flight_at))}
          {field('Last Flight', formatDateTime(summary.last_flight_at))}
        </section>
        {renderKpis([
          { label: 'Flights', value: formatNumber(summary.total_flights) },
          { label: 'Completion', value: completionRate },
          { label: 'Block Hrs', value: formatMinutesAsHours(summary.block_minutes) },
          { label: 'Flight Hrs', value: formatMinutesAsHours(summary.flight_minutes) },
          { label: 'Landings', value: formatNumber(summary.total_landings) },
          { label: 'Complaints', value: formatNumber(complaintStats.total_complaints), tone: complaintStats.open_complaints ? 'attention' : '' },
          { label: 'Delayed Flights', value: formatNumber(delayStats.delayed_flights) },
          { label: 'Delay Time', value: formatDuration(delayStats.delay_minutes) }
        ])}
        <section className="admin-section">
          <div className="admin-section-head"><div><h2>Operational Summary</h2><p>Flight and reporting activity</p></div></div>
          <div className="admin-user-summary-grid">
            {field('Active Flights', formatNumber(summary.active_flights))}
            {field('Aircraft Flown', formatNumber(summary.aircraft_flown))}
            {field('Average Block', formatDuration(summary.average_block_minutes))}
            {field('Average Flight', formatDuration(summary.average_flight_minutes))}
            {field('Full Stop Landings', formatNumber(summary.full_stop_landings))}
            {field('Touch and Go', formatNumber(summary.touch_and_go))}
            {field('Open Complaints', formatNumber(complaintStats.open_complaints))}
            {field('Deferred Complaints', formatNumber(complaintStats.deferred_complaints))}
            {field('Closed Complaints', formatNumber(complaintStats.closed_complaints))}
            {field('Delay Entries', formatNumber(delayStats.delay_entries))}
          </div>
        </section>
        <section className="admin-section">
          <div className="admin-section-head"><div><h2>Most Flown Aircraft</h2><p>User fleet activity</p></div></div>
          <div className="admin-rank-list admin-five-row-list" data-admin-scroll-key={`user-aircraft-${user.id}`}>
            {detail.favoriteAircraft?.length ? detail.favoriteAircraft.map(item => renderRankRow({ ...item, flight_hours: formatMinutesAsHours(item.flight_minutes), flight_cycles: item.total_flights }, 'user', true)) : empty('No aircraft data')}
          </div>
        </section>
        <section className="admin-section">
          <div className="admin-section-head"><div><h2>Aircraft Type Experience</h2><p>Usage grouped by database aircraft type</p></div></div>
          <div className="admin-compact-list admin-five-row-list" data-admin-scroll-key={`user-types-${user.id}`}>
            {detail.aircraftTypeUsage?.length ? detail.aircraftTypeUsage.map(item => (
              <article className="admin-compact-row" key={item.ac_type || 'unknown'}>
                <div><strong>{item.ac_type || 'Unknown type'}</strong><span>Last flown {formatDateTime(item.last_flight_at)}</span></div>
                <div><span>Flights</span><strong>{formatNumber(item.total_flights)}</strong></div>
                <div><span>Completed</span><strong>{formatNumber(item.completed_flights)}</strong></div>
                <div><span>Block Hrs</span><strong>{formatMinutesAsHours(item.block_minutes)}</strong></div>
              </article>
            )) : empty('No aircraft type data')}
          </div>
        </section>
        <section className="admin-section">
          <div className="admin-section-head"><div><h2>Most Flown Routes</h2><p>Route frequency</p></div></div>
          <div className="admin-compact-list admin-five-row-list" data-admin-scroll-key={`user-routes-${user.id}`}>
            {detail.topRoutes?.length ? detail.topRoutes.map((item, index) => (
              <article className="admin-compact-row" key={`${item.from_airport}-${item.to_airport}-${index}`}>
                <div><strong>{item.from_airport || '--'} - {item.to_airport || '--'}</strong><span>Last flown {formatDateTime(item.last_flight_at)}</span></div>
                <div><span>Flights</span><strong>{formatNumber(item.total_flights)}</strong></div>
                <div><span>Completed</span><strong>{formatNumber(item.completed_flights)}</strong></div>
              </article>
            )) : empty('No route data')}
          </div>
        </section>
        <section className="admin-section">
          <div className="admin-section-head"><div><h2>Submitted Complaints</h2><p>Workorders created by this user</p></div></div>
          <div className="admin-user-complaint-list admin-five-row-list" data-admin-scroll-key={`user-complaints-${user.id}`}>
            {detail.authoredComplaints?.length ? detail.authoredComplaints.map(complaint => (
              <article className="admin-user-complaint-row" key={complaint.id}>
                <div><strong>{complaint.work_order_number || `Complaint #${complaint.id}`}</strong><span>{complaint.defect_description || 'No description'}</span></div>
                <div><span>Aircraft</span>{regButton(complaint, complaint.registration, 'user')}</div>
                <div><span>ATA</span><strong>{complaint.ata_chapter || '--'}</strong></div>
                {complaintPill(complaint)}
              </article>
            )) : empty('No submitted complaints')}
          </div>
        </section>
        <section className="admin-section">
          <div className="admin-section-head"><div><h2>Recent Flights</h2><p>User flight records</p></div></div>
          <div className="admin-record-list admin-five-row-list" data-admin-scroll-key={`user-flights-${user.id}`}>{detail.recentFlights?.length ? detail.recentFlights.map(flight => renderFlightRecord(flight, 'user')) : empty('No recent flights')}</div>
        </section>
      </>
    );
  };

  const renderAddAircraftModal = () => addAircraftOpen ? (
    <div className="admin-modal-backdrop" role="presentation" onMouseDown={() => !saving && setAddAircraftOpen(false)}>
      <form className="admin-modal" onSubmit={handleAddAircraft} onMouseDown={event => event.stopPropagation()}>
        <div className="admin-modal-title">Add Aircraft</div>
        {error ? <div className="admin-modal-error">{error}</div> : null}
        <label className="admin-modal-field"><span>Registration</span><input type="text" value={aircraftForm.registration} onChange={event => setAircraftForm(prev => ({ ...prev, registration: event.target.value }))} placeholder="HA-LXX" required autoFocus /></label>
        <label className="admin-modal-field"><span>Aircraft Type</span><select value={aircraftForm.ac_type} onChange={event => setAircraftForm(prev => ({ ...prev, ac_type: event.target.value }))} required><option value="">Select type</option>{typeOptions.map(type => <option value={type} key={type}>{type}</option>)}</select></label>
        <div className="admin-modal-copy">FH and FC start at 0. The verification code is generated automatically.</div>
        <div className="admin-modal-actions"><button type="button" className="admin-btn admin-btn-secondary" onClick={() => setAddAircraftOpen(false)} disabled={saving}>Back</button><button type="submit" className="admin-btn admin-btn-primary" disabled={saving || !aircraftForm.registration.trim() || !aircraftForm.ac_type}>{saving ? 'Adding...' : 'Add Aircraft'}</button></div>
      </form>
    </div>
  ) : null;

  const renderDeferralModal = () => deferralTarget ? (
    <div className="admin-modal-backdrop" role="presentation" onMouseDown={() => !saving && setDeferralTarget(null)}>
      <form className="admin-modal" onSubmit={handleAdminDefer} onMouseDown={event => event.stopPropagation()}>
        <div className="admin-modal-title">Defer {deferralTarget.work_order_number || 'Complaint'}</div>
        {error ? <div className="admin-modal-error">{error}</div> : null}
        <div className="admin-modal-field-grid">
          <label className="admin-modal-field"><span>Category</span><select value={deferralForm.category} onChange={event => setDeferralForm(prev => ({ ...prev, category: event.target.value, deferral_type: event.target.value === 'DD' && !['A', 'D'].includes(prev.deferral_type) ? 'D' : prev.deferral_type }))}><option value="MEL">MEL</option><option value="DD">DD</option></select></label>
          <label className="admin-modal-field"><span>Repair Interval</span><select value={deferralForm.deferral_type} onChange={event => setDeferralForm(prev => ({ ...prev, deferral_type: event.target.value }))}><option value="A">A</option>{deferralForm.category !== 'DD' ? <option value="B">B</option> : null}{deferralForm.category !== 'DD' ? <option value="C">C</option> : null}<option value="D">D</option></select></label>
          <label className="admin-modal-field"><span>Due In Days</span><input type="number" min="1" max="365" inputMode="numeric" value={deferralForm.due_days} onChange={event => setDeferralForm(prev => ({ ...prev, due_days: event.target.value }))} required /></label>
        </div>
        <label className="admin-modal-field"><span>Reason</span><textarea value={deferralForm.reason} onChange={event => setDeferralForm(prev => ({ ...prev, reason: event.target.value }))} placeholder="Admin deferral reason" /></label>
        <div className="admin-modal-actions"><button type="button" className="admin-btn admin-btn-secondary" onClick={() => setDeferralTarget(null)} disabled={saving}>Back</button><button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Defer Item'}</button></div>
      </form>
    </div>
  ) : null;

  if (loading && !overview) return <div className="admin-panel admin-panel-loading"><div className="spinner" /></div>;

  const content = view === 'complaints' ? renderComplaints()
    : view === 'fleet' ? renderFleet()
      : view === 'users' ? renderUsers()
      : view === 'ranking' ? renderRanking()
        : view === 'history' ? renderHistory()
          : view === 'aircraft' ? renderAircraft()
            : view === 'user' ? renderUser()
              : renderOverview();

  return (
    <div className="admin-panel" ref={panelRef} onScrollCapture={handleAdminScroll}>
      {error ? <div className="admin-alert admin-alert-error">{error}</div> : null}
      {message ? <div className="admin-alert admin-alert-ok">{message}</div> : null}
      {content}
      <ConfirmModal action={confirmAction} saving={saving} error={error} onCancel={() => setConfirmAction(null)} onConfirm={handleConfirmAction} />
      {renderAddAircraftModal()}
      {renderDeferralModal()}
    </div>
  );
};

export default AdminPanel;
